/**
 * Everybody in one round, as a spreadsheet — 4 Nov 2026.
 *
 * Asked for as "an export of Emisjon 4 with everybody in it, all info: shares,
 * price, amount paid, date paid". The register's own export (`csv.ts`) is the
 * backup and holds every round packed into one cell; this is the round laid
 * out for reading.
 *
 * **One row per line, not per holder.** A holder can come into a round twice,
 * by a purchase and by exercising an option, at two prices (Emisjon 4 has
 * them). A row per line keeps one price per row, so the price, the amount and
 * the paid date are each one fact; the holder's details repeat on each.
 *
 * **Paid is what the register records.** `paidAt` / `paidDate` say the money
 * landed; the amount is what the shares cost at the agreed price. A difference
 * on the bank statement (a fee abroad, a rounded transfer) is not in the
 * register — `scripts/exportIssuanceRegistration.ts` reads the statement for
 * that.
 *
 * An ongoing round has issued nothing; its rows are the subscriptions, marked
 * as such, with no paid columns.
 *
 * Semicolons and decimal commas without grouping, so Norwegian Excel reads the
 * money as numbers. Pure, so `scripts/checkIssuanceCsv.ts` can run it.
 */
import { effectivePrice, type Issuance, type Shareholder } from '@/types/ownership'

const SEP = ';'

function escape(value: unknown): string {
  const s = value === null || value === undefined ? '' : String(value)
  return /[";\n\r]/.test(s) ? `"${s.split('"').join('""')}"` : s
}

/** "32746,62": a decimal comma and no grouping, so it parses as a number. */
const kr = (n: number | null) => (n === null ? '' : (Math.round(n * 100) / 100 + 0).toFixed(2).replace('.', ','))
/** ISO → dd.mm.yyyy, which Norwegian Excel reads as a date. */
const date = (iso: string | undefined | null) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '')
  return m ? `${m[3]}.${m[2]}.${m[1]}` : ''
}

export const ISSUANCE_CSV_COLUMNS = [
  'Navn',
  'Juridisk navn',
  'Type',
  'Fødselsnummer',
  'Fødselsdato/merknad',
  'Organisasjonsnummer',
  'Kontaktperson',
  'E-post',
  'Telefon',
  'Adresse',
  'Postnummer',
  'Poststed',
  'Land',
  'VPS-konto',
  'Emisjon',
  'Måte',
  'Dato',
  'Antall aksjer',
  'Kurs',
  'Avtalt kurs',
  'Tegningsbeløp',
  'Aksjekapital',
  'Overkurs',
  'Status',
  'Innbetalt beløp',
  'Innbetalt dato',
  'Merknad',
] as const

interface Line {
  how: string
  date: string
  shares: number
  price: number | null
  agreed: boolean
  /** Null for a subscription: nothing has been issued to pay for. */
  paidAt: string | null
  note: string
}

function linesOf(h: Shareholder, iss: Issuance): Line[] {
  const out: Line[] = []
  for (const p of h.purchases.filter((x) => x.issuanceId === iss.id)) {
    out.push({
      how: 'Tegning',
      date: p.date || iss.date,
      shares: p.shares || 0,
      price: effectivePrice(p, iss),
      agreed: p.pricePerShare !== null && p.pricePerShare !== undefined,
      paidAt: p.paidAt ?? '',
      note: p.note ?? '',
    })
  }
  for (const o of h.options.filter((x) => x.exerciseIssuanceId === iss.id && x.status === 'exercised')) {
    out.push({
      how: 'Opsjonsinnløsning',
      date: o.exerciseDate || iss.date,
      shares: o.count || 0,
      price: o.strikePrice,
      agreed: true,
      paidAt: o.paidDate || '',
      note: o.exerciseResolution || '',
    })
  }
  for (const s of (h.subscriptions ?? []).filter((x) => x.issuanceId === iss.id)) {
    out.push({
      how: 'Tegning (ikke utstedt)',
      date: '',
      shares: s.shares || 0,
      price: s.pricePerShare ?? iss.pricePerShare,
      agreed: s.pricePerShare !== null && s.pricePerShare !== undefined,
      paidAt: null,
      note: s.note ?? '',
    })
  }
  return out
}

/**
 * The round's rows, largest holding first — the order of the tegningsliste in
 * the board minutes, so the two can be read side by side.
 */
export function issuanceCsv(iss: Issuance, holders: Shareholder[], parValue: number | null): string {
  const groups = holders
    .map((h) => ({ h, lines: linesOf(h, iss) }))
    .filter((g) => g.lines.length > 0)
    .sort(
      (a, b) =>
        b.lines.reduce((n, l) => n + l.shares, 0) - a.lines.reduce((n, l) => n + l.shares, 0) ||
        (a.h.legalName || a.h.name).localeCompare(b.h.legalName || b.h.name, 'nb'),
    )

  const rows: string[] = []
  for (const { h, lines } of groups) {
    const company = h.kind === 'company'
    for (const l of lines) {
      const amount = l.price === null ? null : l.shares * l.price
      const capital = parValue === null ? null : l.shares * parValue
      const paid = l.paidAt === null ? null : Boolean(l.paidAt)
      rows.push(
        [
          h.name,
          h.legalName ?? '',
          company ? 'Selskap' : 'Person',
          company ? '' : h.personnummer,
          h.birthNote ?? '',
          company ? h.orgNumber : '',
          h.contactPerson ?? '',
          h.email,
          h.phone,
          h.address,
          (h.postalCode ?? '').replace(/\.0+$/, ''),
          h.city ?? '',
          h.country ?? '',
          h.vpsAccount ?? '',
          iss.name,
          l.how,
          date(l.date),
          String(l.shares),
          kr(l.price),
          l.agreed ? 'Ja' : 'Nei',
          kr(amount),
          kr(capital),
          capital === null || amount === null ? '' : kr(amount - capital),
          paid === null ? 'Ikke utstedt' : paid ? 'Betalt' : 'Ubetalt',
          paid ? kr(amount) : '',
          paid ? date(l.paidAt) : '',
          l.note,
        ]
          .map(escape)
          .join(SEP),
      )
    }
  }
  return [ISSUANCE_CSV_COLUMNS.join(SEP), ...rows].join('\r\n') + '\r\n'
}

/** `emisjon-4-2026-11-04.csv` — the round's name, made safe for a file. */
export function issuanceCsvName(iss: Issuance, todayIso: string): string {
  const slug = iss.name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return `${slug || 'emisjon'}-${todayIso}.csv`
}
